import React, { useState } from 'react';
import { useRef } from 'react';
import defaultFlowJson from '../../assets/booking/data/waFormData.json';
import { ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';
import IframeModal from '../../components/appointments/iframeModal';
import { useNotifications } from '../../components/appointments/notifications';

const AppointmentSetupPage = ({ accountFromUrl, acctId }) => {
  const { showSuccess, showError, NotificationComponent } = useNotifications();
  const [copied, setCopied] = useState(false); // Track copy button state
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeTitle, setIframeTitle] = useState('');
  const [flowJson, setFlowJson] = useState(defaultFlowJson);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [flowId, setFlowId] = useState('');
  const [flowName, setFlowName] = useState('');
  const [copiedField, setCopiedField] = useState('');

  // Use acctId prop directly for all backend calls

  // Fetch flowJson from endpoint using acctId prop
  React.useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!acctId) {
          console.warn('AppointmentSetupPage: No account ID found for URL account:', accountFromUrl);
          setFlowJson(defaultFlowJson);
          setLoading(false);
          return;
        }
        console.log('AppointmentSetupPage: Fetching flow for account ID:', acctId);
        const response = await fetch(`/api/wa/wa_appointment_flow_info/${encodeURIComponent(acctId)}`);
        if (response.ok) {
          const data = await response.json();
          if (data && Object.keys(data).length > 0) {
            // waAppointmentFlow is a string, parse to JSON if possible
            let parsedFlow = defaultFlowJson;
            if (typeof data.waAppointmentFlow === 'string') {
              try {
                parsedFlow = JSON.parse(data.waAppointmentFlow);
              } catch (e) {
                parsedFlow = defaultFlowJson;
              }
            } else if (typeof data.waAppointmentFlow === 'object') {
              parsedFlow = data.waAppointmentFlow;
            }
            setFlowJson(parsedFlow);
            setFlowId(data.waAppointmentFlowId || '');
            setFlowName(data.waAppointmentFlowName || '');
          } else {
            setFlowJson(defaultFlowJson);
            setFlowId('');
            setFlowName('');
          }
        } else {
          setFlowJson(defaultFlowJson);
          setFlowId('');
          setFlowName('');
        }
      } catch (err) {
        setFlowJson(defaultFlowJson);
        setFlowId('');
        setFlowName('');
      } finally {
        setLoading(false);
      }
    };
    fetchFlow();
  }, [acctId, accountFromUrl]); // Re-run when acctId changes

  // Function to copy JSON to clipboard
  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(flowJson, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
      })
      .catch(err => {
        console.error('Error copying to clipboard:', err);
      });
  };

  // Function to publish JSON to /publishFlow endpoint
  const handlePublish = async () => {
    // Validate JSON before publishing
    try {
      JSON.stringify(flowJson);
    } catch (e) {
      showError('Invalid JSON: ' + e.message);
      return;
    }
    setPublishing(true);
    if (!acctId) {
      showError('No account ID available for publishing');
      setPublishing(false);
      return;
    }
    try {
      const response = await fetch(`/api/wa/publish_flow/${accountFromUrl}/${acctId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ flow: JSON.stringify(flowJson) }),
      });
      if (response.ok) {
        const res = await response.json();
        if (res && res.facebook && res.facebook.success) {
          setFlowId(res.facebook.id || '');
          setFlowName(res.facebook.flowName || '');
          showSuccess(`Flow published to WABA successfully!`);
        } else {
          showError('Something went wrong, please try after sometime.');
        }
      } else {
        showError('Something went wrong, please try after sometime.');
      }
    } catch (error) {
      showError('Something went wrong, please try after sometime.');
      console.error(error);
    } finally {
      setPublishing(false);
    }
  };

  // Copy handler for Flow ID and Flow Name
  const handleCopyField = (value, field) => {
    navigator.clipboard.writeText(value)
      .then(() => {
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 1500);
      })
      .catch(err => {
        console.error('Error copying to clipboard:', err);
      });
  };

  // Function to open iframe in modal
  const openInIframe = (url, title) => {
    setIframeUrl(url);
    setIframeTitle(title);
    setIframeModalOpen(true);
  };

  return (
    <>
      <NotificationComponent />
      <div className="bg-white rounded-lg shadow-md p-6 relative">
        {/* Flow Id, Flow Name, and Publish button - fluid, aligned, decorative */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4 p-4 bg-gray-50 rounded-md border border-gray-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
            <span className="text-gray-700 font-semibold whitespace-nowrap">Flow ID:</span>
            <span className="flex items-center gap-1 text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 min-w-[80px] text-center shadow-inner text-xs sm:text-sm">
              {flowId || '-'}
              {flowId && (
                <button
                  onClick={() => handleCopyField(flowId, 'id')}
                  className="ml-1 p-1 rounded hover:bg-gray-200 focus:outline-none"
                  title="Copy Flow ID"
                >
                  {copiedField === 'id' ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <ClipboardIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              )}
            </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
            <span className="text-gray-700 font-semibold whitespace-nowrap">Flow Name:</span>
            <span className="flex items-center gap-1 text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 min-w-[120px] text-center shadow-inner text-xs sm:text-sm">
              {flowName || '-'}
              {flowName && (
                <button
                  onClick={() => handleCopyField(flowName, 'name')}
                  className="ml-1 p-1 rounded hover:bg-gray-200 focus:outline-none"
                  title="Copy Flow Name"
                >
                  {copiedField === 'name' ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <ClipboardIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              )}
            </span>
            </div>
          </div>
          <div className="flex justify-end w-full sm:w-auto">
            <button
              onClick={handlePublish}
              className={`flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-700 text-white px-3 py-1.5 rounded font-semibold shadow-md hover:from-green-600 hover:to-green-800 transition-all duration-200 text-sm ${publishing ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Publish JSON"
              aria-label="Publish JSON"
              disabled={publishing}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>

        {/* JSON Editor */}
        {loading ? (
          <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">Loading flow data...</div>
        ) : (
          <div className="relative">
            <pre className="bg-gray-100 p-4 rounded-md text-sm overflow-auto">
              {JSON.stringify(flowJson, null, 2)}
            </pre>
          </div>
        )}
        {/* Load mask overlay when publishing */}
        {publishing && (
          <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10 rounded-lg">
            <span className="text-lg font-semibold text-gray-700">Publishing...</span>
          </div>
        )}
      </div>

      {/* Iframe Modal for WhatsApp Preview */}
      <IframeModal
        isOpen={iframeModalOpen}
        onClose={() => setIframeModalOpen(false)}
        src={iframeUrl}
        title={iframeTitle}
      />
    </>
  );
};

export default AppointmentSetupPage;
